import { useState, useMemo } from "react"
import { Alert, Box, Button, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import { useParams } from "react-router"
import { navigate } from "lib/routes/navigate"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { Bound } from "lib/@components/binding/Bound"
import { Repeat } from "lib/@components/repeat"
import { If, Switch, Case } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { useDialog } from "lib/@hooks/useDialog"
import { scenario } from "routes/scenarios/controller/scenarios"
import { recalculateVariant as recalculateVariantApi } from "routes/scenarios/controller/recalculate-variant"
import { promoteScenarioToDraftRegime } from "routes/scenarios/controller/promote-scenario"
import { exportComparisonToExcel } from "./export-comparison-to-excel"
import { runScenarioCostExport } from "./run-scenario-cost-export"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { PromoteScenarioDialog } from "./promote-scenario-dialog"
import { usePromotionNotifications } from "./use-promotion-notifications"
import { PromotedRegimeBanner } from "./promoted-regime-banner"
import { VariantComparisonColumn } from "./variant-comparison-column"
import { VariantComparisonChip } from "./variant-comparison-chip"
import { VariantComparisonPicker } from "./variant-comparison-picker"
import { ComparisonTradeBreakdown } from "./comparison-trade-breakdown"
import { ComparisonFacilityBreakdown } from "./comparison-facility-breakdown"
import { useDemand } from "lib/authorization/use-demand"

export function VariantComparison() {
    const { id } = useParams()
    const data = scenario.useResults({ id }, withInitialValue(IS_LOADING))
    const canManageOptimiser = useDemand("manageScenarios")
    const [selectedIds, setSelectedIds] = useState(null)
    const [viewTab, setViewTab] = useState("overview")
    const { promotedRegimeId, clearPromotedRegime, notifyPromotionStarted } = usePromotionNotifications(id)

    const openPicker = useDialog(<VariantComparisonPicker />, { maxWidth: "xs", fullWidth: true })
    const openPromoteDialog = useDialog(<PromoteScenarioDialog />, { maxWidth: "sm", fullWidth: true })

    const variants = useMemo(getVariants, [data?.variants])
    const comparedIds = useMemo(getComparedIds, [selectedIds, variants])
    const comparedVariants = useMemo(getComparedVariants, [comparedIds, variants])
    const baselineVariant = comparedVariants.length >= 2 ? comparedVariants[0] : null
    const lowestCostId = useMemo(getLowestCostId, [comparedVariants])
    const highestCostId = useMemo(getHighestCostId, [comparedVariants])
    const mostCoverageId = useMemo(getMostCoverageId, [comparedVariants])
    const hasCurrencyMismatch = useMemo(checkCurrencyMismatch, [comparedVariants])

    const parentCrumbs = useMemo(getParentCrumbs, [data, id])
    const comparisonActions = getComparisonActions()

    if (data === IS_LOADING) return <LoadingScreen description="Loading scenario..." />

    if (!data) {
        return (
            <PageBreadcrumbContainer title="Scenario Not Found" parentCrumbs={parentCrumbs} sx={{ height: 1 }}>
                <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, p: 3 }}>
                    <Typography variant="h6" color="text.secondary">
                        This scenario could not be found.
                    </Typography>
                </Stack>
            </PageBreadcrumbContainer>
        )
    }

    return (
        <PageBreadcrumbContainer title="Variant Comparison" parentCrumbs={parentCrumbs} sx={{ height: 1 }}>
            <Stack spacing={3} sx={{ px: 3, pt: 2, flex: 1, pb: 3 }}>
                <If value={promotedRegimeId}>
                    <PromotedRegimeBanner regimeId={promotedRegimeId} onDismiss={clearPromotedRegime} />
                </If>
                <Bound onRemoveVariant={handleRemoveVariant} onEditVariant={handleEditVariant}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", gap: 1 }}>
                        <Repeat list={comparedVariants} item={<VariantComparisonChip />} />
                        <If value={comparedVariants.length < variants.length}>
                            <IconButton
                                aria-label="Add variant"
                                size="small"
                                onClick={handleAddVariant}
                                sx={{ bgcolor: "action.hover", width: 32, height: 32 }}
                            >
                                <Iconify icon="mdi:plus" />
                            </IconButton>
                        </If>
                        <Box flex={1} />
                        <If value={comparedVariants.length >= 2}>
                            <Button
                                data-cy="comparison-export"
                                variant="outlined"
                                size="small"
                                startIcon={<Iconify icon="mdi:download" />}
                                onClick={handleExportComparison}
                            >
                                Export Comparisons
                            </Button>
                        </If>
                    </Stack>
                </Bound>

                <If value={hasCurrencyMismatch}>
                    <Alert severity="warning">
                        Variants use different currencies. Cost comparisons may be misleading.
                    </Alert>
                </If>

                <If value={comparedVariants.length >= 2}>
                    <ToggleButtonGroup
                        data-cy="comparison-view-tabs"
                        value={viewTab}
                        exclusive
                        onChange={handleChangeTab}
                        size="small"
                        sx={{ alignSelf: "flex-start" }}
                    >
                        <ToggleButton data-cy="comparison-view-tab-overview" value="overview">
                            Overview
                        </ToggleButton>
                        <ToggleButton data-cy="comparison-view-tab-trades" value="trades">
                            Trades
                        </ToggleButton>
                        <ToggleButton data-cy="comparison-view-tab-facilities" value="facilities">
                            Facilities
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Switch value={viewTab}>
                        <Case when="overview">
                            <Box sx={{ display: "flex", gap: 4, overflowX: "auto", pb: 2 }}>
                                <Bound
                                    onEditVariant={handleEditVariant}
                                    onRecalculateVariant={comparisonActions.onRecalculateVariant}
                                    onSetAsDefault={handleSetAsDefault}
                                    onPromoteVariant={comparisonActions.onPromoteVariant}
                                    onViewTab={setViewTab}
                                    lowestCostId={lowestCostId}
                                    highestCostId={highestCostId}
                                    mostCoverageId={mostCoverageId}
                                >
                                    <Repeat
                                        list={comparedVariants}
                                        item={
                                            <VariantComparisonColumn baseline={baselineVariant} scenarioId={data.id} />
                                        }
                                        keyFn={getVariantKey}
                                    />
                                </Bound>
                            </Box>
                        </Case>
                        <Case when="trades">
                            <ComparisonTradeBreakdown variants={comparedVariants} />
                        </Case>
                        <Case when="facilities">
                            <ComparisonFacilityBreakdown variants={comparedVariants} />
                        </Case>
                    </Switch>
                    <Else>
                        <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 6 }}>
                            <Iconify icon="mdi:compare-horizontal" sx={{ fontSize: 48, opacity: 0.3 }} />
                            <Stack alignItems="center" spacing={0.5}>
                                <Typography variant="body1" color="text.secondary">
                                    You need at least two variants to compare.
                                </Typography>
                                <If value={variants.length < 2}>
                                    <Typography variant="body2" color="text.secondary">
                                        Add more variants to this scenario to start comparing.
                                    </Typography>
                                    <Else>
                                        <Typography variant="body2" color="text.secondary">
                                            Use the + button above to add another variant to the comparison.
                                        </Typography>
                                    </Else>
                                </If>
                            </Stack>
                            <If value={variants.length < 2}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Iconify icon="mdi:arrow-left" />}
                                    onClick={handleBackToScenario}
                                >
                                    Back to Scenario
                                </Button>
                            </If>
                        </Stack>
                    </Else>
                </If>
            </Stack>
        </PageBreadcrumbContainer>
    )

    function getVariants() {
        return data?.variants ?? []
    }

    function getParentCrumbs() {
        return [
            { label: "Optimiser", to: "/app/optimiser" },
            { label: data?.name ?? "Scenario", to: `/app/optimiser/scenarios/${id}` },
        ]
    }

    function getComparedIds() {
        if (selectedIds) return selectedIds
        return variants.slice(0, 2).map((v) => v.id)
    }

    function getComparedVariants() {
        return comparedIds.map((vid) => variants.find((v) => v.id === vid)).filter(Boolean)
    }

    function getLowestCostId() {
        if (comparedVariants.length < 2) return null
        let lowestId = null
        let lowestCost = Infinity
        let tie = false
        for (const v of comparedVariants) {
            const cost = v?.costResult?.grandTotal ?? v?.annualCost ?? Infinity
            if (cost < lowestCost) {
                lowestCost = cost
                lowestId = v.id
                tie = false
            } else if (cost === lowestCost) {
                tie = true
            }
        }
        if (tie) return null
        if (lowestCost === Infinity) return null
        return lowestId
    }

    function getHighestCostId() {
        if (comparedVariants.length < 2) return null
        let highestId = null
        let highestCost = -Infinity
        let tie = false
        for (const v of comparedVariants) {
            const cost = v?.costResult?.grandTotal ?? v?.annualCost ?? -Infinity
            if (cost > highestCost) {
                highestCost = cost
                highestId = v.id
                tie = false
            } else if (cost === highestCost) {
                tie = true
            }
        }
        if (tie) return null
        if (highestCost === -Infinity) return null
        return highestId
    }

    function getMostCoverageId() {
        if (comparedVariants.length < 2) return null
        let bestId = null
        let bestCoverage = -1
        let tie = false
        for (const v of comparedVariants) {
            const coverage = v?.costResult?.costedTaskCount ?? -1
            if (coverage > bestCoverage) {
                bestCoverage = coverage
                bestId = v.id
                tie = false
            } else if (coverage === bestCoverage) {
                tie = true
            }
        }
        if (tie) return null
        if (bestCoverage <= 0) return null
        return bestId
    }

    function getVariantKey(variant) {
        return variant.id
    }

    function handleRemoveVariant(variantId) {
        const next = comparedIds.filter(isNotRemoved)
        setSelectedIds(next)

        function isNotRemoved(vid) {
            return vid !== variantId
        }
    }

    async function handleAddVariant() {
        const available = variants.filter(isNotAlreadyCompared)
        const picked = await openPicker({ variants: available })
        if (!picked) return
        setSelectedIds([...comparedIds, picked.id])

        function isNotAlreadyCompared(v) {
            return !comparedIds.includes(v.id)
        }
    }

    async function handleExportComparison() {
        const withDetailsRef = comparedVariants.filter(hasDetailsRef)
        // All database-backed: export server-side. All legacy: keep the in-browser
        // workbook. Mixed would silently produce empty sheets for the database-backed
        // variants, so ask for a recalculation instead.
        if (withDetailsRef.length === comparedVariants.length) {
            await runScenarioCostExport({
                scenarioId: data.id,
                variantIds: comparedVariants.map(toVariantId),
                mode: "comparison",
            })
            return
        }
        if (withDetailsRef.length > 0) {
            errorSnackbar("Recalculate every compared variant before exporting the comparison")
            return
        }
        exportComparisonToExcel({ scenario: data, variants: comparedVariants })

        function hasDetailsRef(variant) {
            return !!variant.costResult?.detailsRef?.setKey
        }

        function toVariantId(variant) {
            return variant.id
        }
    }

    function handleChangeTab(_, newTab) {
        if (newTab != null) setViewTab(newTab)
    }

    function handleBackToScenario() {
        navigate(`/app/optimiser/scenarios/${id}`)
    }

    function handleSetAsDefault(variantId) {
        const next = [variantId, ...comparedIds.filter(isNotDefault)]
        setSelectedIds(next)

        function isNotDefault(vid) {
            return vid !== variantId
        }
    }

    function handleEditVariant(__variant) {
        // Placeholder: comparison view is read-only, but bound context passes this through
    }

    async function handleRecalculateVariant(variantId) {
        await recalculateVariantApi({ scenarioId: data.id, variantId })
    }

    async function handlePromoteVariant(variant) {
        const result = await openPromoteDialog({ scenario: data, variant })
        if (!result) return
        // Kick off the promotion; the heavy tailoring runs in the background and we get notified
        // (via usePromotionNotifications) when the draft regime is ready.
        const promoted = await promoteScenarioToDraftRegime({ scenarioId: data.id, ...result })
        if (!promoted?.regimeId) return
        notifyPromotionStarted(promoted.regimeName)
    }

    function checkCurrencyMismatch() {
        if (comparedVariants.length < 2) return false
        const currencies = new Set(comparedVariants.map(getVariantCurrency))
        return currencies.size > 1
    }

    function getVariantCurrency(v) {
        return v.costResult?.currency || v.rateCardCurrency || "GBP"
    }

    function getComparisonActions() {
        if (!canManageOptimiser) return {}

        return {
            onRecalculateVariant: handleRecalculateVariant,
            onPromoteVariant: handlePromoteVariant,
        }
    }
}
